"use client";

import { useState, useCallback } from "react";
import {
  isOAuthError,
  type OAuthError,
} from "@/lib/accounting/oauth-error-handler";

interface UseOAuthErrorHandlerOptions {
  venueId: string | number;
  defaultAccountingSystem?: string;
}

interface UseOAuthErrorHandlerReturn {
  oauthError: OAuthError | null;
  showReauthDialog: boolean;
  setShowReauthDialog: (show: boolean) => void;
  checkAndHandleOAuthError: (error: any) => boolean;
  accountingSystem: string;
}

/**
 * Hook to handle OAuth errors and show re-authorization dialog
 *
 * @example
 * ```tsx
 * const { checkAndHandleOAuthError, showReauthDialog, setShowReauthDialog, accountingSystem } =
 *   useOAuthErrorHandler({ venueId: 5, defaultAccountingSystem: 'fortnox' });
 *
 * try {
 *   const response = await fetch('/api/...');
 *   const data = await response.json();
 *   if (!data.success) {
 *     if (!checkAndHandleOAuthError(data.error)) {
 *       // Handle other errors
 *       toast.error(data.error);
 *     }
 *   }
 * } catch (error) {
 *   checkAndHandleOAuthError(error);
 * }
 *
 * return (
 *   <>
 *     <OAuthReauthDialog
 *       open={showReauthDialog}
 *       onOpenChange={setShowReauthDialog}
 *       accountingSystem={accountingSystem}
 *       venueId={venueId}
 *     />
 *   </>
 * );
 * ```
 */
export function useOAuthErrorHandler({
  venueId,
  defaultAccountingSystem = "fortnox",
}: UseOAuthErrorHandlerOptions): UseOAuthErrorHandlerReturn {
  const [oauthError, setOauthError] = useState<OAuthError | null>(null);
  const [showReauthDialog, setShowReauthDialog] = useState(false);

  /**
   * Check if an error is an OAuth error and handle it
   * Returns true if it was an OAuth error, false otherwise
   */
  const checkAndHandleOAuthError = useCallback((error: any): boolean => {
    const result = isOAuthError(error);

    if (result.isOAuthError) {
      setOauthError(result);
      setShowReauthDialog(true);
      return true;
    }

    return false;
  }, []);

  const accountingSystem =
    oauthError?.accountingSystem || defaultAccountingSystem;

  return {
    oauthError,
    showReauthDialog,
    setShowReauthDialog,
    checkAndHandleOAuthError,
    accountingSystem,
  };
}
